import { trpc } from '../../lib/trpc';
import { usePortalBrand } from '../../contexts/PortalBrandContext';
import type { NotificationPage } from '@globalbridge/types';

/**
 * Notifications hooks (`notifications.*`, apps/api/src/notifications). The
 * selected brand is always sent: the server ignores it for a brand user's
 * own brand check (membership-validated) and requires it for GB staff.
 */

const STALE_MS = 60 * 1000;

export interface ListFilter {
  page?: NotificationPage;
  unreadOnly?: boolean;
  doneBefore?: string;
}

function useBrandId(): number | undefined {
  const { selectedBrand } = usePortalBrand();
  return selectedBrand?.snowflakeIdNum;
}

/** The grouped list (Needs you / Updates / Done) for the selected brand. */
export function useNotificationsList(filter: ListFilter, opts: { enabled?: boolean } = {}) {
  const brandId = useBrandId();
  return trpc.notifications.list.useQuery(
    { brandId, ...filter },
    { staleTime: STALE_MS, enabled: brandId != null && opts.enabled !== false, placeholderData: (prev) => prev },
  );
}

/** The bell: `needsYou` is the badge (Ari 2026-10-08), `unread` the sub-line. */
export function useNotificationsBadge() {
  const brandId = useBrandId();
  return trpc.notifications.badge.useQuery(
    { brandId },
    { staleTime: STALE_MS, refetchInterval: 5 * STALE_MS, enabled: brandId != null },
  );
}

/** Marks read; refreshes the list and the badge. Never call it while impersonating. */
export function useMarkNotificationsRead() {
  const brandId = useBrandId();
  const utils = trpc.useUtils();
  const m = trpc.notifications.markRead.useMutation({
    onSuccess: () => {
      void utils.notifications.list.invalidate();
      void utils.notifications.badge.invalidate();
    },
  });
  return {
    markIds: (ids: string[]) => (ids.length > 0 ? m.mutate({ brandId, ids }) : undefined),
    markAll: () => m.mutate({ brandId, all: true }),
    isPending: m.isPending,
  };
}

/** The viewer's own email switches ("Just you"). */
export function useNotificationPreferences() {
  return trpc.notifications.preferences.useQuery(undefined, { staleTime: 5 * STALE_MS });
}

export function useSetNotificationPreference() {
  const utils = trpc.useUtils();
  return trpc.notifications.setPreference.useMutation({
    onSuccess: (data) => utils.notifications.preferences.setData(undefined, data),
  });
}
